import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  ApiError,
  useApi,
  useApiWithErrors,
  Tabs,
  TabsItem,
  Gutter,
} from '@axiom/ui';
import type { Candidate } from '@axiom/validation';

import type { WorkFeedViewsType } from '../../utils/WorkFeedUtil';
import { WORK_FEED_VIEWS, WorkFeedUtil } from '../../utils/WorkFeedUtil';
import { WorkFeedFilterPills } from '../WorkFeedFilterPills/WorkFeedFilterPills';
import { WorkFeedApi } from '../../api/work-feed-api';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';

import { WorkFeedCards } from './WorkFeedCards';

export type WorkFeedBodyProps = {
  candidate: Candidate;
  currentView: WorkFeedViewsType;
  filters: Record<string, unknown>;
  pageNumber: number;
  name?: string;
};
export const WorkFeedBody = ({
  candidate,
  currentView,
  filters,
  pageNumber,
  name = 'WORK_FEED_BODY',
}: WorkFeedBodyProps) => {
  const navigate = useNavigate();

  const getSearchFilters = () => {
    return {
      page: pageNumber,
      pageSize: 10,
      filters,
    };
  };

  const [search, saved] = useApiWithErrors(
    WorkFeedApi.readWorkFeed(candidate.id, getSearchFilters()),
    WorkFeedApi.readWorkFeedSaved(candidate.id, {
      page: pageNumber,
      pageSize: 10,
    })
  );

  const [{ data: profileIssues }] = useApi(
    CandidateProfileIssuesApi.readCandidateProfileIssues(candidate.id)
  );

  const getCurrentOpportunities = () => {
    return WorkFeedUtil.isSavedView(currentView) ? saved : search;
  };

  useEffect(() => {
    const data = [search, saved];
    if (data.some(res => res instanceof ApiError)) {
      for (const datum of data) {
        if (datum instanceof ApiError) {
          const error = datum as ApiError;
          if (error.getStatusCode() === 403) {
            const failUrl = `../403`;
            void navigate(failUrl, { replace: true });
            return;
          }
          throw datum;
        }
      }
    } else if (profileIssues.isSubmissionReady) {
      const currentOpportunities = getCurrentOpportunities();
      // navigate to last page if user goes over pageCount
      if (pageNumber > 1 && pageNumber > currentOpportunities.meta.pageCount) {
        const queryParams = window.location.search || '';
        const pageIndicator =
          currentOpportunities.meta.pageCount > 1
            ? `/page/${currentOpportunities.meta.pageCount}`
            : '';

        void navigate(`.${pageIndicator}${queryParams}`, { replace: true });
      }
    } else {
      const actionNeededUrl = '../actionNeeded';
      void navigate(actionNeededUrl, { replace: true });
    }
  }, [search, saved, pageNumber, currentView, profileIssues]);

  if ([search, saved].some(res => res instanceof ApiError)) {
    return null;
  }

  return (
    <Tabs name={name} startTab={currentView}>
      <TabsItem
        name={WORK_FEED_VIEWS.all}
        label="All"
        onClick={() => {
          void navigate('../all');
        }}
      >
        <Gutter bottom="16px" />
        <WorkFeedFilterPills
          name="WORK_FEED_FILTER_PILLS_WRAPPER"
          filters={filters}
          candidate={candidate}
        />
        <WorkFeedCards
          workFeedItems={search.data || undefined}
          resultCount={search.meta.resultCount}
          candidate={candidate}
          meta={search.meta}
          view={currentView}
          name="PENDO_WORK_FEED_CARDS"
        />
      </TabsItem>
      <TabsItem
        name={WORK_FEED_VIEWS.saved}
        label="Saved"
        countBadge={saved.meta.resultCount}
        onClick={() => {
          void navigate('../saved');
        }}
      >
        <Gutter bottom="16px" />
        <WorkFeedCards
          workFeedItems={saved.data || undefined}
          resultCount={saved.meta.resultCount}
          candidate={candidate}
          meta={saved.meta}
          view={currentView}
          name="PENDO_WORK_FEED_CARDS"
        />
      </TabsItem>
    </Tabs>
  );
};
